'use client';

import React from 'react';
import { X, RotateCcw } from 'lucide-react';

interface Filters {
  city: string;
  bhk: string;
  minRent: string;
  maxRent: string;
  houseType: string;
  furnishing: string;
  availability: string;
}

interface PropertyFiltersProps {
  filters: Filters;
  onChange: (f: Filters) => void;
  onReset: () => void;
  activeCount: number;
  onClose: () => void;
}

const CITIES = ['Mumbai', 'Pune', 'Bangalore', 'Hyderabad', 'Chennai', 'Delhi', 'Noida', 'Gurgaon', 'Ahmedabad', 'Kolkata'];
const BHK_OPTIONS = ['1 RK', '1 BHK', '2 BHK', '3 BHK', '4 BHK', '4+ BHK'];
const HOUSE_TYPES = ['Apartment', 'Independent House', 'Villa', 'Room', 'Studio'];
const FURNISHING_OPTIONS = ['Fully Furnished', 'Semi Furnished', 'Unfurnished'];
const RENT_RANGES = [
  { label: 'Under ₹10,000', min: '0', max: '10000' },
  { label: '₹10,000 – ₹20,000', min: '10000', max: '20000' },
  { label: '₹20,000 – ₹35,000', min: '20000', max: '35000' },
  { label: '₹35,000 – ₹60,000', min: '35000', max: '60000' },
  { label: 'Above ₹60,000', min: '60000', max: '' },
];

export default function PropertyFilters({ filters, onChange, onReset, activeCount, onClose }: PropertyFiltersProps) {
  const update = (key: keyof Filters, value: string) => {
    onChange({ ...filters, [key]: value });
  };

  const setRentRange = (min: string, max: string) => {
    onChange({ ...filters, minRent: min, maxRent: max });
  };

  const currentRentLabel = RENT_RANGES.find(
    (r) => r.min === filters.minRent && r.max === filters.maxRent
  )?.label;

  return (
    <div className="bg-card rounded-2xl border border-border card-shadow p-5 sticky top-24 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-bold text-foreground tracking-wide">Filters</h3>
          {activeCount > 0 && (
            <span className="bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">
              {activeCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {activeCount > 0 && (
            <button
              onClick={onReset}
              className="btn-ghost text-xs gap-1 px-2 py-1"
              aria-label="Reset all filters"
              suppressHydrationWarning
            >
              <RotateCcw size={12} />
              Reset
            </button>
          )}
          <button
            onClick={onClose}
            className="lg:hidden p-1.5 rounded-lg hover:bg-muted text-muted-foreground"
            aria-label="Close filters"
            suppressHydrationWarning
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* City */}
      <div>
        <label className="label-text">City</label>
        <select
          value={filters.city}
          onChange={(e) => update('city', e.target.value)}
          className="input-field"
          aria-label="Filter by city"
          suppressHydrationWarning
        >
          <option value="">All Cities</option>
          {CITIES.map((c) => (
            <option key={`city-${c}`} value={c}>{c}</option>
          ))}
        </select>
      </div>

      {/* BHK Type */}
      <div>
        <label className="label-text">BHK Type</label>
        <div className="flex flex-wrap gap-2 mt-1">
          {BHK_OPTIONS.map((b) => (
            <button
              key={`bhk-${b}`}
              onClick={() => update('bhk', filters.bhk === b ? '' : b)}
              suppressHydrationWarning
              className={`px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all duration-150 ${
                filters.bhk === b
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-muted text-muted-foreground border-border hover:border-primary hover:text-primary'
              }`}
            >
              {b}
            </button>
          ))}
        </div>
      </div>

      {/* House Type */}
      <div>
        <label className="label-text">Property Type</label>
        <div className="flex flex-col gap-1.5 mt-1">
          {HOUSE_TYPES.map((t) => (
            <label
              key={`type-${t}`}
              className="flex items-center gap-2.5 cursor-pointer group"
            >
              <input
                type="radio"
                name="houseType"
                value={t}
                checked={filters.houseType === t}
                onChange={() => update('houseType', filters.houseType === t ? '' : t)}
                className="accent-primary w-4 h-4"
              />
              <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                {t}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Rent Range */}
      <div>
        <label className="label-text">Monthly Rent</label>
        <div className="flex flex-col gap-1.5 mt-1">
          {RENT_RANGES.map((r) => (
            <button
              key={`rent-${r.label}`}
              onClick={() => {
                if (currentRentLabel === r.label) {
                  setRentRange('', '');
                } else {
                  setRentRange(r.min, r.max);
                }
              }}
              suppressHydrationWarning
              className={`text-left px-3 py-2 rounded-xl text-xs font-semibold border transition-all duration-150 ${
                currentRentLabel === r.label
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-muted text-muted-foreground border-border hover:border-primary hover:text-primary'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* Furnishing */}
      <div>
        <label className="label-text">Furnishing</label>
        <div className="flex flex-col gap-1.5 mt-1">
          {FURNISHING_OPTIONS.map((f) => (
            <label
              key={`furn-${f}`}
              className="flex items-center gap-2.5 cursor-pointer group"
            >
              <input
                type="checkbox"
                checked={filters.furnishing === f}
                onChange={() => update('furnishing', filters.furnishing === f ? '' : f)}
                className="accent-primary w-4 h-4 rounded"
              />
              <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                {f}
              </span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}